import { learningLabSchema } from '@mio/shared';
import { useId, useState } from 'react';

import { SelectField } from '@/shared/ui/select';

import { applicationModels, applicationModelTitles } from './application-models';
import { employeeInput, employeeSeed, httpExchange } from './concept-models';
import { DiscoveryExperiment } from './discovery-experiment';
import {
  ApplicationMap,
  PythonProgram,
  TestBench,
  Persistence,
  RestContract,
} from './foundation-models';

import './learning-lab.css';

import type { LearningLabKind } from '@mio/shared';
import type { ReactNode } from 'react';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="lab-field">
      <span>{label}</span>
      {children}
    </label>
  );
}
function Output({ children }: { children: ReactNode }) {
  return (
    <div className="lab-observation" role="status">
      {children}
    </div>
  );
}
function Records({
  rows,
  match,
}: {
  rows: ReturnType<typeof employeeSeed>;
  match?: (row: ReturnType<typeof employeeSeed>[number]) => boolean;
}) {
  return (
    <table className="lab-records">
      <thead>
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Active</th>
          {match ? <th>Matches</th> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} data-match={match?.(row)}>
            <td>{row.id}</td>
            <td>{row.name}</td>
            <td>{String(row.active)}</td>
            {match ? <td>{match(row) ? 'Yes' : 'No'}</td> : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Roles() {
  const [choice, setChoice] = useState('requirements');
  const cases = {
    requirements: [
      'Agree the required employee actions and unresolved policies.',
      'Identify product capabilities and implementation constraints.',
      'Map the need to a feasible solution and deployment context.',
    ],
    integration: [
      'Build and verify the customer-specific adapter.',
      'Provide supported APIs and reusable product changes.',
      'Review how the integration fits the surrounding systems.',
    ],
    incident: [
      'Reproduce the issue in the customer environment and explain evidence.',
      'Investigate product defects and implement fixes.',
      'Check architecture assumptions and operational responsibilities.',
    ],
  } as const;
  const values = cases[choice as keyof typeof cases];
  return (
    <>
      <Field label="Responsibility">
        <SelectField
          aria-label="Responsibility"
          value={choice}
          onValueChange={setChoice}
          options={[
            { value: 'requirements', label: 'Clarify the customer requirement' },
            { value: 'integration', label: 'Implement an integration' },
            { value: 'incident', label: 'Diagnose a production incident' },
          ]}
        />
      </Field>
      <div className="lab-role-list">
        {['FDE', 'Software engineer', 'Solutions architect'].map((role, i) => (
          <div key={role}>
            <strong>{role}</strong>
            <p>{values[i]}</p>
          </div>
        ))}
      </div>
      <Output>Responsibilities overlap; agree ownership with the actual team.</Output>
    </>
  );
}
function Requirements() {
  const examples = [
    [
      'There are 10,000 employee records.',
      'Fact',
      'Population does not establish concurrent usage.',
    ],
    [
      'Only the approved region may store employee data.',
      'Constraint',
      'This limits acceptable storage and deployment choices.',
    ],
    [
      'We will assume ten simultaneous HR users for the prototype.',
      'Assumption',
      'Confirm the workload before treating this as an agreed target.',
    ],
    [
      'A blank name is rejected and record count stays unchanged.',
      'Acceptance criterion',
      'The starting input and observable outcome can guide a check.',
    ],
  ];
  const [index, setIndex] = useState(0),
    [answer, setAnswer] = useState(''),
    [revealed, setRevealed] = useState(false);
  const item = examples[index] ?? ['', '', ''];
  return (
    <>
      <Field label="Statement">
        <SelectField
          aria-label="Statement"
          value={index}
          onValueChange={(value) => {
            setIndex(Number(value));
            setRevealed(false);
            setAnswer('');
          }}
          options={examples.map((_, i) => ({ value: i, label: `Statement ${i + 1}` }))}
        />
      </Field>
      <blockquote>{item[0]}</blockquote>
      <div className="lab-actions">
        {['Fact', 'Constraint', 'Assumption', 'Acceptance criterion'].map((label) => (
          <button
            key={label}
            aria-pressed={answer === label}
            onClick={() => {
              setAnswer(label);
              setRevealed(false);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <button disabled={!answer} onClick={() => setRevealed(true)}>
        Check classification
      </button>
      {revealed ? (
        <Output>
          {answer === item[1] ? 'Correct.' : `This is a ${item[1]?.toLowerCase()}.`} {item[2]}
        </Output>
      ) : null}
    </>
  );
}
function Values() {
  const [string, setString] = useState(false),
    [selected, setSelected] = useState('active');
  const flag = string ? 'false' : false;
  return (
    <>
      <div className="lab-actions">
        <button aria-pressed={!string} onClick={() => setString(false)}>
          Boolean False
        </button>
        <button aria-pressed={string} onClick={() => setString(true)}>
          String "false"
        </button>
      </div>
      <div className="lab-two">
        <div className="lab-tree">
          <strong>employees [list]</strong>
          <span>└ 0 [dictionary]</span>
          {['id', 'name', 'active'].map((key) => (
            <button key={key} aria-pressed={selected === key} onClick={() => setSelected(key)}>
              └ {key}: {key === 'id' ? '7' : key === 'name' ? '"Asha"' : JSON.stringify(flag)}
            </button>
          ))}
        </div>
        <div>
          <h4>Selected expression</h4>
          <pre>{`employees[0]["${selected}"]`}</pre>
          <dl>
            <dt>Type</dt>
            <dd>
              {selected === 'id'
                ? 'integer'
                : selected === 'name'
                  ? 'string'
                  : string
                    ? 'string'
                    : 'boolean'}
            </dd>
            <dt>if active</dt>
            <dd>{string ? 'branch entered' : 'branch skipped'}</dd>
            <dt>active is True</dt>
            <dd>False</dd>
          </dl>
        </div>
      </div>
      <Output>
        {string
          ? 'Nonempty text is truthy, even when it spells “false”.'
          : 'The boolean False does not enter the conditional branch.'}
      </Output>
    </>
  );
}
function PythonTrace() {
  const [input, setInput] = useState('name'),
    [step, setStep] = useState(0);
  const value =
    input === 'number'
      ? 42
      : input === 'blank'
        ? '   '
        : input === 'long'
          ? 'a'.repeat(81)
          : '  Asha  ';
  const sequence =
    input === 'number' ? [0, 1, 2] : input === 'name' ? [0, 1, 3, 4, 6] : [0, 1, 3, 4, 5];
  const at = sequence[step] ?? 0,
    done = step === sequence.length - 1;
  const lines = [
    'def normalize_name(value):',
    '    if not isinstance(value, str):',
    '        raise TypeError',
    '    name = value.strip()',
    '    if not 1 <= len(name) <= 80:',
    '        raise ValueError',
    '    return name',
  ];
  const result = done
    ? input === 'number'
      ? 'TypeError'
      : input === 'name'
        ? '"Asha"'
        : 'ValueError'
    : 'not returned';
  return (
    <>
      <div className="lab-controls">
        <Field label="Input">
          <SelectField
            aria-label="Input"
            value={input}
            onValueChange={(value) => {
              setInput(value);
              setStep(0);
            }}
            options={[
              { value: 'name', label: '" Asha "' },
              { value: 'blank', label: 'Whitespace only' },
              { value: 'number', label: '42 (integer)' },
              { value: 'long', label: '81 characters' },
            ]}
          />
        </Field>
        <button disabled={step === 0} onClick={() => setStep(step - 1)}>
          Previous
        </button>
        <button disabled={done} onClick={() => setStep(step + 1)}>
          Next step
        </button>
      </div>
      <div className="lab-two">
        <pre className="lab-trace">
          {lines.map((line, i) => (
            <code key={line} aria-current={i === at ? 'step' : undefined}>
              <span>{i + 1}</span>
              {line}
            </code>
          ))}
        </pre>
        <div>
          <h4>After this line</h4>
          <dl>
            <dt>value</dt>
            <dd>{JSON.stringify(value)}</dd>
            <dt>name</dt>
            <dd>{at >= 3 ? JSON.stringify(String(value).trim()) : 'not assigned'}</dd>
            <dt>result</dt>
            <dd>{result}</dd>
          </dl>
        </div>
      </div>
      <Output>
        Step {step + 1}/{sequence.length} ·{' '}
        {done
          ? result.includes('Error')
            ? 'The exception exits the function before return.'
            : 'The caller receives the normalized name.'
          : at === 3
            ? 'Trimming creates name without changing the input.'
            : 'Follow the condition to see which branch executes next.'}
      </Output>
    </>
  );
}
function GitStaging() {
  const [working, setWorking] = useState('80'),
    [staged, setStaged] = useState('100'),
    [committed, setCommitted] = useState('100'),
    [commits, setCommits] = useState(1);
  return (
    <>
      <Field label="Working-tree name limit">
        <input
          type="number"
          min="1"
          max="1000"
          value={working}
          onChange={(e) => setWorking(e.target.value)}
        />
      </Field>
      <div className="lab-snapshots">
        {[
          ['Working tree', working],
          ['Staging area', staged],
          ['Latest commit', committed],
        ].map(([label, value]) => (
          <div key={label}>
            <h4>{label}</h4>
            <pre>{`MAX_NAME = ${value}`}</pre>
          </div>
        ))}
      </div>
      <div className="lab-actions">
        <button onClick={() => setStaged(working)}>Stage current file</button>
        <button
          disabled={staged === committed}
          onClick={() => {
            setCommitted(staged);
            setCommits(commits + 1);
          }}
        >
          Commit staged version
        </button>
      </div>
      <div className="lab-two">
        <div>
          <h4>git diff</h4>
          <pre>
            {working === staged
              ? 'No unstaged changes'
              : `- MAX_NAME = ${staged}\n+ MAX_NAME = ${working}`}
          </pre>
        </div>
        <div>
          <h4>git diff --staged</h4>
          <pre>
            {staged === committed
              ? 'No staged changes'
              : `- MAX_NAME = ${committed}\n+ MAX_NAME = ${staged}`}
          </pre>
        </div>
      </div>
      <Output>
        {commits} snapshot(s) · editing the working tree does not change an already staged version.
      </Output>
    </>
  );
}
function JsonInspector() {
  const [text, setText] = useState('{"name":"Asha","active":false}');
  let parsed: unknown,
    error = '';
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    error = 'Syntax error: expected valid JSON with double-quoted keys and values where required.';
  }
  const validated = employeeInput(text);
  return (
    <>
      <Field label="JSON text">
        <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} />
      </Field>
      <div className="lab-two">
        <div>
          <h4>Parsed structure</h4>
          <pre>{error || JSON.stringify(parsed, null, 2)}</pre>
        </div>
        <div>
          <h4>Employee validation</h4>
          <pre>
            {typeof validated === 'string' ? validated : JSON.stringify(validated, null, 2)}
          </pre>
        </div>
      </div>
      <div className="lab-actions">
        <button onClick={() => setText("{'name':'Asha'}")}>Try invalid syntax</button>
        <button onClick={() => setText('{"name":42}')}>Try wrong field type</button>
        <button onClick={() => setText('{"name":"Asha","active":false}')}>Try valid data</button>
      </div>
      <Output>
        {error
          ? 'No employee object can be validated yet.'
          : typeof validated === 'string'
            ? 'JSON syntax is valid; the application contract rejects these values.'
            : 'The schema accepted this record. False remains a boolean.'}
      </Output>
    </>
  );
}
function HttpInspector({ matrix = false }: { matrix?: boolean }) {
  const [rows, setRows] = useState(employeeSeed),
    [method, setMethod] = useState('GET'),
    [path, setPath] = useState('/api/employees/7'),
    [body, setBody] = useState('{"name":"  Ravi  ","active":true}'),
    [running, setRunning] = useState(true);
  const [response, setResponse] = useState('Not sent'),
    [note, setNote] = useState('Predict the status and whether the store changes.'),
    [patchBody, setPatchBody] = useState('{"active":false}');
  function send(m = method, p = path) {
    const before = rows.length,
      result = httpExchange(rows, m, p, body, running);
    if (m === 'PATCH')
      setPatchBody(JSON.stringify({ active: !rows.find((r) => r.id === 7)?.active }));
    setRows(result.rows);
    setResponse(
      result.status === null
        ? String(result.body)
        : `HTTP ${result.status}\n${result.status === 204 ? '(no body)' : JSON.stringify(result.body, null, 2)}`,
    );
    setNote(
      `Records: ${before} → ${result.rows.length}. ${result.status === null ? 'No HTTP status exists.' : result.status >= 400 ? 'Inspect the error boundary.' : 'Compare the returned representation and state.'}`,
    );
  }
  return (
    <>
      {matrix ? (
        <div className="lab-actions">
          {[
            ['GET', '/api/employees'],
            ['POST', '/api/employees'],
            ['GET', '/api/employees/7'],
            ['PATCH', '/api/employees/7'],
            ['DELETE', '/api/employees/7'],
          ].map(([m, p]) => (
            <button
              key={`${m}${p}`}
              onClick={() => {
                setMethod(m ?? 'GET');
                setPath(p ?? '/api/employees');
                send(m, p);
              }}
            >
              {m} {p?.endsWith('/7') ? 'employee 7' : 'collection'}
            </button>
          ))}
        </div>
      ) : (
        <div className="lab-controls">
          <Field label="Method">
            <SelectField
              aria-label="Method"
              value={method}
              onValueChange={(value) => {
                setMethod(value);
                setResponse('Not sent');
              }}
              options={[
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
              ]}
            />
          </Field>
          <Field label="Path">
            <SelectField
              aria-label="Path"
              value={path}
              onValueChange={(value) => {
                setPath(value);
                setResponse('Not sent');
              }}
              options={[
                { value: '/api/employees/7', label: '/api/employees/7' },
                { value: '/api/employees/9999', label: '/api/employees/9999' },
                { value: '/api/employees', label: '/api/employees' },
              ]}
            />
          </Field>
          <Field label="Server">
            <SelectField
              aria-label="Server"
              value={running ? 'running' : 'stopped'}
              onValueChange={(value) => {
                setRunning(value === 'running');
                setResponse('Not sent');
              }}
              options={[
                { value: 'running', label: 'Running' },
                { value: 'stopped', label: 'Stopped' },
              ]}
            />
          </Field>
        </div>
      )}
      {method === 'POST' ? (
        <Field label="JSON body">
          <textarea
            value={body}
            rows={2}
            onChange={(e) => {
              setBody(e.target.value);
              setResponse('Not sent');
            }}
          />
        </Field>
      ) : null}
      <div className="lab-two">
        <div>
          <h4>Request</h4>
          <pre>{`${method} ${path}\n${method === 'POST' ? 'Content-Type: application/json\n\n' + body : method === 'PATCH' ? 'Content-Type: application/json\n\n' + patchBody : 'Accept: application/json'}`}</pre>
        </div>
        <div>
          <h4>Response</h4>
          <pre>{response}</pre>
        </div>
      </div>
      {!matrix ? <button onClick={() => send()}>Send request</button> : null}
      {matrix ? (
        <>
          <Records rows={rows} />
          <button
            onClick={() => {
              setRows(employeeSeed());
              setResponse('Not sent');
              setNote('Teaching store reset to its two fixtures.');
            }}
          >
            Reset teaching store
          </button>
        </>
      ) : null}
      <Output>{note}</Output>
    </>
  );
}
function FastApiTrace() {
  const [choice, setChoice] = useState('valid');
  const invalid = choice === 'invalid',
    missing = choice === 'missing';
  const steps = [
    'Match method and path',
    'Parse and validate fields',
    'Execute handler',
    'Call repository',
    'Serialize response',
  ];
  return (
    <>
      <Field label="Request">
        <SelectField
          aria-label="Request"
          value={choice}
          onValueChange={setChoice}
          options={[
            { value: 'valid', label: 'POST a valid employee' },
            { value: 'invalid', label: 'POST a whitespace-only name' },
            { value: 'missing', label: 'GET employee 9999' },
          ]}
        />
      </Field>
      <ol className="lab-boundaries">
        {steps.map((step, i) => (
          <li key={step} data-stopped={invalid && i > 1}>
            <span>{i + 1}</span>
            <div>
              <strong>{step}</strong>
              <small>
                {invalid && i > 1
                  ? 'Not executed'
                  : invalid && i === 1
                    ? 'Name constraint fails'
                    : missing && i === 3
                      ? 'Lookup returns None'
                      : missing && i === 4
                        ? 'HTTPException → 404'
                        : i === 4
                          ? '201 and the created employee'
                          : 'Reached'}
              </small>
            </div>
          </li>
        ))}
      </ol>
      <pre>
        {invalid
          ? 'EmployeeCreate rejects the name\n# store.create is never called'
          : missing
            ? 'record = store.get(9999)  # None\nraise HTTPException(404, "Employee not found")'
            : '@router.post("", status_code=201)\ndef create_employee(employee: EmployeeCreate):\n    return store.create(employee.name, employee.active)'}
      </pre>
      <Output>
        {invalid ? '422 · zero writes' : missing ? '404 · zero writes' : '201 · one record created'}{' '}
        in this modeled request.
      </Output>
    </>
  );
}
function SqlWorkbench() {
  const [rows, setRows] = useState(employeeSeed),
    [predicate, setPredicate] = useState('id'),
    [action, setAction] = useState('select'),
    [result, setResult] = useState('Select a predicate; inspect matching rows before running.');
  const matches = (row: ReturnType<typeof employeeSeed>[number]) =>
    predicate === 'all' || (predicate === 'id' ? row.id === 7 : !row.active);
  const clause =
    predicate === 'all' ? '' : `\nWHERE ${predicate === 'id' ? 'id = 7' : 'active = false'}`;
  return (
    <>
      <div className="lab-controls">
        <Field label="Statement">
          <SelectField
            aria-label="Statement"
            value={action}
            onValueChange={setAction}
            options={[
              { value: 'select', label: 'SELECT' },
              { value: 'update', label: 'UPDATE active = false' },
            ]}
          />
        </Field>
        <Field label="WHERE condition">
          <SelectField
            aria-label="WHERE condition"
            value={predicate}
            onValueChange={setPredicate}
            options={[
              { value: 'id', label: 'id = 7' },
              { value: 'inactive', label: 'active = false' },
              { value: 'all', label: 'No WHERE' },
            ]}
          />
        </Field>
      </div>
      <pre>
        {(action === 'select'
          ? 'SELECT id, name, active\nFROM employees'
          : 'UPDATE employees\nSET active = false') +
          clause +
          (action === 'select' ? '\nORDER BY id;' : '\nRETURNING id, name, active;')}
      </pre>
      <Records rows={rows} match={matches} />
      <div className="lab-actions">
        <button
          onClick={() => {
            const count = rows.filter(matches).length;
            if (action === 'update')
              setRows(rows.map((row) => (matches(row) ? { ...row, active: false } : row)));
            setResult(
              action === 'update'
                ? `UPDATE ${count} · matching rows processed.`
                : `SELECT returned ${count} row(s); data unchanged.`,
            );
          }}
        >
          Run statement
        </button>
        <button
          onClick={() => {
            setRows(employeeSeed());
            setResult('Two seed rows restored in this model.');
          }}
        >
          Reset teaching rows
        </button>
      </div>
      <Output>{result}</Output>
    </>
  );
}
function FrontendStates() {
  const [name, setName] = useState('Ravi'),
    [condition, setCondition] = useState('success'),
    [rows, setRows] = useState(employeeSeed),
    [pending, setPending] = useState(false),
    [message, setMessage] = useState('Ready'),
    [response, setResponse] = useState('No request');
  async function save() {
    setPending(true);
    setMessage('Saving…');
    await new Promise((resolve) => setTimeout(resolve, 900));
    if (condition !== 'success') {
      setResponse(condition === 'validation' ? '422 · invalid input' : 'No HTTP response');
      setMessage('Save failed. Input retained; no row added.');
    } else {
      const input = employeeInput(JSON.stringify({ name, active: true }));
      if (typeof input === 'string') {
        setResponse('422');
        setMessage(input);
      } else {
        const row = { id: Math.max(8, ...rows.map((r) => r.id)) + 1, ...input };
        setRows([...rows, row]);
        setResponse(`201 · ${JSON.stringify(row)}`);
        setName('');
        setMessage('Saved. The server-confirmed row is displayed.');
      }
    }
    setPending(false);
  }
  return (
    <>
      <Field label="Response condition">
        <SelectField
          aria-label="Response condition"
          disabled={pending}
          value={condition}
          onValueChange={setCondition}
          options={[
            { value: 'success', label: 'Success after a short delay' },
            { value: 'validation', label: 'Validation rejection' },
            { value: 'network', label: 'Service stopped' },
          ]}
        />
      </Field>
      <div className="lab-application">
        <Field label="Employee name">
          <input disabled={pending} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <button disabled={pending} onClick={() => void save()}>
          {pending ? 'Saving…' : 'Save employee'}
        </button>
        <p role="status">{message}</p>
        <Records rows={rows} />
      </div>
      <pre>{response}</pre>
      <Output>Compare pending state, retained input and the record list.</Output>
    </>
  );
}
function Debugging() {
  const [scenario, setScenario] = useState('database'),
    [evidence, setEvidence] = useState<string[]>([]),
    [answer, setAnswer] = useState('');
  const observations =
    scenario === 'database'
      ? {
          'HTTP response': '503 — Database temporarily unavailable.',
          'API health': '200 — the API process responds.',
          'API log': 'GET /api/employees: database connection unavailable.',
          'Process status': 'API running; training PostgreSQL stopped.',
        }
      : {
          'HTTP response': 'Connection refused — no HTTP status.',
          'API health': 'Connection refused.',
          'API log': 'No matching request reached the API process.',
          'Process status': 'API stopped; PostgreSQL running.',
        };
  return (
    <>
      <Field label="Case">
        <SelectField
          aria-label="Case"
          value={scenario}
          onValueChange={(value) => {
            setScenario(value);
            setEvidence([]);
            setAnswer('');
          }}
          options={[
            { value: 'database', label: 'Directory cannot load' },
            { value: 'api', label: 'Health request cannot connect' },
          ]}
        />
      </Field>
      <div className="lab-actions">
        {Object.keys(observations).map((key) => (
          <button
            key={key}
            disabled={evidence.includes(key)}
            onClick={() => setEvidence([...evidence, key])}
          >
            Inspect {key.toLowerCase()}
          </button>
        ))}
      </div>
      <ul className="lab-evidence">
        {evidence.map((key) => (
          <li key={key}>
            <strong>{key}</strong>
            <span>{observations[key as keyof typeof observations]}</span>
          </li>
        ))}
      </ul>
      <Field label="Which boundary failed in this case?">
        <SelectField
          aria-label="Which boundary failed in this case?"
          value={answer}
          onValueChange={setAnswer}
          options={[
            { value: '', label: 'Choose after inspecting evidence' },
            { value: 'database', label: 'API → database' },
            { value: 'api', label: 'Client → API process' },
          ]}
        />
      </Field>
      {answer ? (
        <Output>
          {evidence.length === 0
            ? 'Inspect evidence before concluding.'
            : answer === scenario
              ? 'Consistent with this case. State which observation rules out the alternative.'
              : 'Revisit the HTTP and process observations; that boundary does not explain both.'}
        </Output>
      ) : null}
    </>
  );
}
const titles: Record<LearningLabKind, string> = {
  ...applicationModelTitles,
  'application-map': 'Inside a web application',
  'python-program': 'Watch Python execute',
  'test-bench': 'Which tests catch the defect?',
  persistence: 'What survives a restart?',
  'fde-roles': 'Responsibilities in practice',
  requirements: 'From statement to criterion',
  'python-values': 'Inspect the shape and type',
  'python-trace': 'Trace the function',
  'git-staging': 'Inspect the three snapshots',
  'json-inspector': 'Text → values → valid employee',
  'http-inspector': 'Inspect an HTTP exchange',
  'rest-matrix': 'Operate on the resource',
  'fastapi-trace': 'Follow the request into code',
  'sql-workbench': 'See which rows match',
  'frontend-states': 'Observe the browser state',
  debugging: 'Choose the next observation',
};
const models: Record<LearningLabKind, () => ReactNode> = {
  ...applicationModels,
  'application-map': ApplicationMap,
  'python-program': PythonProgram,
  'test-bench': TestBench,
  persistence: Persistence,
  'fde-roles': Roles,
  requirements: Requirements,
  'python-values': Values,
  'python-trace': PythonTrace,
  'git-staging': GitStaging,
  'json-inspector': JsonInspector,
  'http-inspector': () => <HttpInspector />,
  'rest-matrix': RestContract,
  'fastapi-trace': FastApiTrace,
  'sql-workbench': SqlWorkbench,
  'frontend-states': FrontendStates,
  debugging: Debugging,
};
export function LearningLab({ source }: { source: string }) {
  const id = useId();
  let input: unknown;
  try {
    input = JSON.parse(source) as unknown;
  } catch {
    input = null;
  }
  const parsed = learningLabSchema.safeParse(input);
  if (!parsed.success)
    return (
      <aside className="concept-lab" role="alert">
        This interactive could not load. The written explanation and examples remain available.
      </aside>
    );
  const Model = models[parsed.data.kind];
  return (
    <section className="concept-lab" data-kind={parsed.data.kind} aria-labelledby={id}>
      <header>
        <span>EXPLORE THE CONCEPT</span>
        <h3 id={id}>{titles[parsed.data.kind]}</h3>
      </header>
      {parsed.data.exploration ? (
        <DiscoveryExperiment key={source} exploration={parsed.data.exploration}>
          <Model />
        </DiscoveryExperiment>
      ) : (
        <Model />
      )}
    </section>
  );
}
